import { useEffect, useState, Fragment, Suspense } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useObjectsProvider } from '../../context/objects';
import { useLocationState } from '../../context/location';
import { useIsMount } from '../../hooks/useIsMount';
import { categoryCaseConverter, declOfNum, JSONToQuery, sleep } from '../../helpers';
import Skelet from '../Skelet';
import ObjectsCatalogSort from './ObjectsCatalogSort';
import ObjectsCatalogSkeletonItem from './ObjectsCatalogSkeletonItem';
import ObjectsCatalogCommercialItem from './ObjectsCatalogCommercialItem';
import css from '../../styles/modules/objects/ObjectsCatalog.module.scss';
import Map from '../../public/images/map.svg';

const generatePagination = (current, last) => {
  const delta = 1;
  const left = current - delta;
  const right = current + delta + 1;
  const range = [];
  const rangeWithDots = [];

  let l;

  for (let i = 1; i <= last; i += 1) {
    if (i === 1 || i === last || (i >= left && i < right)) {
      range.push(i);
    }
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const i of range) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l !== 1) {
        rangeWithDots.push(null);
      }
    }
    rangeWithDots.push(i);
    l = i;
  }

  return rangeWithDots;
};

const ObjectsCatalogItem = dynamic(() => import('./ObjectsCatalogItem'), { suspense: true });

const ObjectsCatalog = () => {
  /** STATES */
  const router = useRouter();
  const isMount = useIsMount();
  const locationState = useLocationState();
  const [objectsState, setObjectsState] = useObjectsProvider();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState(null);
  const [total, setTotal] = useState(null);
  const [page, setPage] = useState(null);
  const [pages, setPages] = useState(null);

  /** HANDLERS AND FUNCTIONS */
  const handlePagingClick = (value, modificator = 0) => {
    if (value + modificator === page || value + modificator <= 0 || value + modificator > pages)
      return;

    setPage(value + modificator);
    setObjectsState({ type: 'SET_PAGINATION', payload: { page: value + modificator } });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const isSortSelected = (sort, dir) => {
    if (!sort)
      return false;

    if (objectsState.sorting.sort === sort) {
      if (dir) {
        if (objectsState.sorting.sortDirection === dir)
          return true;

        return false;
      }

      return true;
    }

    return false;
  };
  const handleMoveToMap = () => {
    router.push(
      `/${locationState.code}/objects/map${JSONToQuery(
        { ...objectsState.filter, ...objectsState.pagination, ...objectsState.sorting },
        [
          'length',
          objectsState.filter.studio && !objectsState.filter.rooms.length && 'studio',
          !objectsState.filter.studio && objectsState.filter.rooms.length === 1 && 'rooms'
        ],
        ['page=1', 'sort=last-update.date']
      )}`,
      undefined,
      {
        scroll: false
      }
    );
  };

  /** HOOKS */
  useEffect(() => {
    if (!locationState.isLoaded)
      return;

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      try {
        setIsLoading(true);
        await sleep(300);

        const response = await axios.post(process.env.NEXT_PUBLIC_API + '/objects', {
          ...objectsState.filter,
          ...objectsState.sorting,
          ...objectsState.pagination,
          location: locationState.region
        }, { signal });
  
        if (!response.data || response.data.status !== 'ok')
          throw new Error(response.data.text);
  
        setTotal(response.data.data.total);
        setPage(response.data.data.current_page);
        setPages(response.data.data.last_page);
        setData(response.data.data.data);
        setIsLoading(false);
        setIsError(false);
      } catch (error) {
        if (error.code !== 'ERR_CANCELED') {
          setIsLoading(false);
          setIsError(true);
        }
      }
    };
    fetchData();

    return () => controller.abort();
  }, [objectsState.filter, locationState]);
  useEffect(() => {
    if (!locationState.isLoaded || isMount || isLoading)
      return;

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      try {
        setIsLoadingData(true);
        await sleep(300);
  
        const response = await axios.post(process.env.NEXT_PUBLIC_API + '/objects', {
          ...objectsState.filter,
          ...objectsState.sorting,
          ...objectsState.pagination,
          location: locationState.region
        }, { signal });
  
        if (!response.data || response.data.status !== 'ok')
          throw new Error(response.data.text);
  
        setPage(response.data.data.current_page);
        setData(response.data.data.data);
        setIsLoadingData(false);
        setIsError(false);
      } catch (error) {
        if (error.code !== 'ERR_CANCELED') {
          setIsLoadingData(false);
          setIsError(true);
        }
      }
    };
    fetchData();

    return () => controller.abort();
  }, [objectsState.pagination]);
  useEffect(() => window.scrollTo({ top: 0, behavior: 'smooth' }), [locationState]);

  /** TEMPLATES */
  if (isLoading || isError)
    return (
      <section className={['block', css.clear].join(' ')}>
        <div className={['fixed', css.catalog].join(' ')}>
          <div className={css.catalogHeader}>
            <Skelet height={32} maxWidth={400} className={css.catalogHeaderTitle} />
            <Skelet height={32} maxWidth={200} className={css.catalogHeaderActions} />
          </div>
          <div className={css.catalogContent}>
            {[...Array(3).keys()].map((value) => (
              <ObjectsCatalogSkeletonItem key={value} />
            ))}
          </div>
          <Skelet
            height={48}
            maxWidth={300}
            className={css.catalogFooter}
            style={{ margin: 'auto' }}
          />
        </div>
      </section>
    );

  return (
    <section className={['block', css.clear].join(' ')}>
      <div className={['fixed', css.catalog].join(' ')}>
        {data.length > 0 && (
          <div className={css.catalogHeader}>
            <div className={css.catalogHeaderTitle}>
              {declOfNum(total, [
                objectsState.filter.category === 'дом' || objectsState.filter.category === 'участок' ? 'Найден' : 'Найдена',
                'Найдено',
                'Найдено'
              ])} {total} {categoryCaseConverter(objectsState.filter.category, total)}
              <span> в {locationState.case}</span>
            </div>
            <div className={css.catalogHeaderActions}>
              <button type="button" className={css.catalogMapButton} onClick={handleMoveToMap}>
                <div style={{ marginRight: 8 }}>На карте</div>
                <Map />                  
              </button>
              <ObjectsCatalogSort
                content={[
                  {
                    text: 'По дате публикации',
                    sort: 'last-update.date',
                    selected: isSortSelected('last-update.date')
                  },
                  {
                    text: 'Сначала дешевле',
                    sort: 'price.value',
                    direction: 'asc',
                    selected: isSortSelected('price.value', 'asc')
                  },
                  {
                    text: 'Сначала дороже',
                    sort: 'price.value',
                    direction: 'desc',
                    selected: isSortSelected('price.value', 'desc')
                  }
                ]}
              />
            </div>
          </div>
        )}
        <div className={css.catalogContent}>
          {isLoadingData ? (
            [...Array(3).keys()].map((value) => <ObjectsCatalogSkeletonItem key={value} />)
          ) : data.length > 0 ? (
            data.map((value, index) => {
              if (data.length > 4 && index === 3) {
                return (
                  <Fragment key={index}>
                    <Suspense fallback={<ObjectsCatalogSkeletonItem />}>
                      <ObjectsCatalogItem item={value} />
                    </Suspense>
                    {/* <ObjectsCatalogCommercialItem /> */}
                  </Fragment>
                );
              }

              return (
                <Suspense fallback={<ObjectsCatalogSkeletonItem />} key={index}>
                  <ObjectsCatalogItem item={value} />
                </Suspense>
              );
            })
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{
                opacity: 1,
                scale: 1,
                transition: { duration: 1, type: 'spring', bounce: 0.6 }
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <h5 style={{ marginBottom: 8 }}>Нет вариантов в {locationState.case}</h5>
              <div style={{ opacity: 0.6, textAlign: 'center', margin: '0 18px' }}>
                Попробуйте изменить фильтры или расширить область поиска
              </div>
              <img
                src="/images/no_results.svg"
                alt=""
                style={{ width: '100%', maxWidth: 400, margin: '18px 32px' }}
              />
            </motion.div>
          )}
        </div>
        {pages > 1 && (
          <div className={css.catalogFooter}>
            <button
              className={css.catalogFooterLeft}
              type="button"
              aria-label="Предыдущая страница"
              onClick={() => handlePagingClick(page, -1)}
              style={{
                cursor: page <= 1 ? 'not-allowed' : null,
                opacity: page <= 1 ? 0.1 : null
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            {generatePagination(page, pages).map((value, index) => {
              if (!value) return <span key={index}>...</span>;

              return (
                <button
                  className={[css.catalogFooterButton, value === page ? css.active : ''].join(' ')}
                  type="button"
                  key={index}
                  onClick={() => handlePagingClick(value)}
                  style={{ cursor: value === page ? 'default' : null }}
                >
                  {value}
                </button>
              );
            })}
            <button
              className={css.catalogFooterRight}
              type="button"
              aria-label="Следующая страница"
              onClick={() => handlePagingClick(page, 1)}
              style={{
                cursor: page >= pages ? 'not-allowed' : null,
                opacity: page >= pages ? 0.1 : null
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ObjectsCatalog;
